/* eslint-disable react/react-in-jsx-scope */
import axios from "axios";
import { toast } from "react-toastify";

import BannerContent from "../shared/BannerContent/BannerContent";
import mapToSearchObj from "./mapToSearchObj";

const tenantIds = process.env.EXTRA_TENANT_IDS || "";
const accountIdFilters = process.env.ACCOUNT_ID_FILTERS || "";

export class ApiClient {
  instance;

  tenantIdsToQuery = [];

  accountIdsToFilterBy = [];

  constructor() {
    this.tenantIdsToQuery = [
      process.env.TENANT_ID,
      ...tenantIds.split(","),
    ].filter(Boolean);

    this.accountIdsToFilterBy = [...accountIdFilters.split(",")].filter(
      Boolean
    );

    this.instance = axios.create({
      baseURL: `${process.env.ATS_URL}/js`,
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Tenant: process.env.TENANT_ID,
      },
    });
  }

  async makeRequestsWithMultipleTenantIds(
    endpoint,
    method,
    data,
    mergeStrategy,
    returnFirstValidResponse = false,
    initialAccumulator = { data: { results: [], totalResults: 0 } }
  ) {
    const responses = await Promise.all(
      this.tenantIdsToQuery.map((tenantId) => {
        if (method === "get") {
          return this.instance
            .get(endpoint, {
              headers: { Tenant: tenantId },
            })
            .catch((error) => {
              if (error.response && error.response.status === 503) {
                toast(
                  // eslint-disable-next-line react/jsx-filename-extension
                  <BannerContent message="We are currently doing maintaince. You may not be able to view all jobs." />,
                  {
                    position: "top-center",
                    autoClose: false,
                    closeButton: true,
                    closeOnClick: true,
                  }
                );
              }

              return null; // return null or a default value for failed request
            });
        }
        return this.instance
          .post(endpoint, data, {
            headers: { Tenant: tenantId },
          })
          .catch((error) => {
            if (error.response && error.response.status === 503) {
              toast(
                // eslint-disable-next-line react/jsx-filename-extension
                <BannerContent message="We are currently doing maintaince. You may not be able to view all jobs." />,
                {
                  position: "top-center",
                  autoClose: false,
                  closeButton: true,
                  closeOnClick: true,
                }
              );
            }

            return null; // return null or a default value for failed request
          });
      })
    );
    const validResponses = responses.filter((response) => response !== null);
    if (validResponses.length === 0) {
      throw new Error("All tenant requests failed");
    }

    let mergedData;
    if (returnFirstValidResponse) {
      if (validResponses && validResponses[0] && validResponses[0].data) {
        return validResponses[0];
      }
    } else {
      mergedData = validResponses.reduce(mergeStrategy, initialAccumulator);
    }

    if (data && data.limit) {
      mergedData.data.results = mergedData.data.results.slice(0, data.limit);
    }

    return {
      status: 200, // Assuming success, adjust based on actual logic
      statusText: "OK",
      headers: {}, // Assuming no specific headers, adjust as necessary
      config: {}, // Assuming no specific config needed, adjust as necessary
      request: {}, // Assuming no specific request details needed, adjust as necessary
      data: mergedData.data, // Ensuring data is structured correctly
    };
  }

  getVacancies = async (query, options) => {
    const params = {
      order: query?.order || "DESC",
      sortBy: "createdDateTime",
      ...query,
    };

    if (
      typeof window !== "undefined" &&
      window?.location?.pathname === "/internal-jobs"
    ) {
      params.isInternal = true;
    }

    const searchObj = await mapToSearchObj(
      params,
      options,
      this.accountIdsToFilterBy
    );
    const data = { limit: 5000, ...searchObj };
    const mergeStrategy = (acc, response) => {
      // Initialize acc with default structure if it's not already done
      if (!acc.data || !Array.isArray(acc.data.results)) {
        acc.data = { results: [], totalResults: 0, buckets: {} };
      }

      // Merge results and totalResults as before
      if (response.data.results) {
        acc.data.results = [
          ...acc.data.results,
          ...(response.data.results || []),
        ];
        acc.data.totalResults += response.data.totalResults || 0;
      }

      // Merge buckets
      const responseBuckets = response.data.buckets || {};
      Object.keys(responseBuckets).forEach((bucketName) => {
        if (!acc.data.buckets[bucketName]) {
          acc.data.buckets[bucketName] = [...responseBuckets[bucketName]];
        } else {
          responseBuckets[bucketName].forEach((responseBucketItem) => {
            const existingBucketItem = acc.data.buckets[bucketName].find(
              (item) => item.name === responseBucketItem.name
            );
            if (existingBucketItem) {
              existingBucketItem.count += responseBucketItem.count;
            } else {
              acc.data.buckets[bucketName].push({ ...responseBucketItem });
            }
          });
        }
      });
      return acc;
    };

    console.log("[search] /search/vacancy request", {
      tenantIds: this.tenantIdsToQuery,
      query: data,
    });

    const response = await this.makeRequestsWithMultipleTenantIds(
      "/search/vacancy",
      "post",
      data,
      mergeStrategy,
      false,
      { data: { results: [], totalResults: 0, buckets: {} } }
    );

    console.log("[search] /search/vacancy response", {
      totalResults: response?.data?.totalResults,
      returned: response?.data?.results?.length,
    });

    return response;
  };

  getRoles = async () => {
    const params = {
      order: "DESC",
      sortBy: "createdDateTime",
    };
    const options = {
      fields: ["category"],
      limit: 5000,
    };
    const searchObj = await mapToSearchObj(
      params,
      options,
      this.accountIdsToFilterBy
    );

    const mergeStrategy = (acc, response) => {
      acc.results = [...acc.results, ...(response.data.results || [])];
      acc.totalResults += response.data.totalResults || 0;
      return acc;
    };

    return this.makeRequestsWithMultipleTenantIds(
      "/search/vacancy",
      "post",
      searchObj,
      mergeStrategy
    );
  };

  searchLocation = (input, location) => {
    return this.instance.get(
      `/location/address-lookup?address=${input}&country=${location}`
    );
  };

  getSingleVacancy = async (id) => {
    const response = await this.makeRequestsWithMultipleTenantIds(
      `/vacancy/${id}`,
      "get",
      null,
      null,
      true
    );
    return response;
  };

  reverseGeocode = (coords) => {
    return this.instance.get(
      `/location/reverse-geocode?position=${coords.lat},${coords.long}`
    );
  };

  getCategories = () => {
    const mergeStrategy = (acc, response) => {
      // Merge categories, potentially deduplicating
      const categories = response.data.categories || [];
      acc.categories = [
        ...new Map(
          [...acc.categories, ...categories].map((category) => [
            category.id,
            category,
          ])
        ).values(),
      ];
      return acc;
    };

    return this.makeRequestsWithMultipleTenantIds(
      "vacancy/categories",
      "get",
      null,
      mergeStrategy
    );
  };

  static registerJobAlert = (data, resolvedFunction, catchFunction) => {
    const regInstance = axios.create({
      baseURL: `${process.env.ATS_URL}`,
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Tenant: process.env.TENANT_ID,
      },
    });
    return regInstance
      .post("/th/job-alerts", data)
      .then(resolvedFunction)
      .catch(catchFunction);
  };

  getAllVenues = () => {
    const mergeStrategy = (acc, response) => {
      // Assuming venues can be simply concatenated, adjust as needed
      acc.data = [...acc.data, ...(response.data || [])];
      return acc;
    };

    return this.makeRequestsWithMultipleTenantIds(
      "/company/list",
      "get",
      null,
      mergeStrategy,
      false,
      { data: [] }
    );
  };

  getSingleVenue = (id) => {
    return this.makeRequestsWithMultipleTenantIds(
      `/company/identifier/${id}`,
      "get",
      null,
      null,
      true
    );
  };
}

const apiInstance = new ApiClient();
export default apiInstance;
